import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, API_BASE, getToken } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { Users, UserPlus, X, Search, Check, CheckCheck, Undo2, ClipboardCheck, AlertTriangle, Zap, Download, Loader2, ChevronRight, Plane, Wallet, ExternalLink, Mail, GitBranch, StickyNote, BellRing, Building2 } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { PayPeriodSelector } from "@/components/PayPeriodSelector";
import { roleLabel } from "@/lib/permissions";
import TeamHierarchyView from "@/components/TeamHierarchyView";
import VacationHeadcountBanner from "@/components/VacationHeadcountBanner";

const DEPT_PALETTE = ["#2563EB", "#D97706", "#059669", "#DC2626", "#7C3AED", "#0891B2", "#DB2777", "#65A30D", "#EA580C", "#4F46E5"];

export default function DirectReports() {
  const { user } = useAuth();
  const [reports, setReports] = useState([]);
  const [available, setAvailable] = useState([]);
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);

  // Approvals (period view)
  const [currentPeriod, setCurrentPeriod] = useState(null);
  const [summary, setSummary] = useState({ rows: [], pay_period: null });
  const [approvalSearch, setApprovalSearch] = useState("");
  const [downloadingUserId, setDownloadingUserId] = useState(null);
  const [expandedRowIds, setExpandedRowIds] = useState(new Set());
  const toggleRow = (id) => setExpandedRowIds((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  // L2 hierarchy toggle — only rendered for Senior Managers + Admins. State
  // stored in localStorage so the leader's preference persists between visits.
  const canSeeHierarchy = user?.role === "admin" || user?.job_title === "Senior Manager";
  const [showHierarchy, setShowHierarchy] = useState(() => {
    try { return localStorage.getItem("sa_team_show_hierarchy") === "1"; } catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem("sa_team_show_hierarchy", showHierarchy ? "1" : "0"); } catch { /* ignore */ }
  }, [showHierarchy]);

  const load = async () => {
    // Use allSettled so a 403 on /direct-reports (e.g. a delegate who has no
    // reports of their own but IS a stand-in for someone with reports) still
    // lets currentPeriod resolve → /team/summary → the delegated rows render.
    const results = await Promise.allSettled([
      api.get("/direct-reports"),
      api.get("/direct-reports/available"),
      currentPeriod ? Promise.resolve(null) : api.get("/pay-periods/current"),
    ]);
    const [r, a, p] = results;
    if (r.status === "fulfilled") setReports(r.value.data);
    if (a.status === "fulfilled") setAvailable(a.value.data);
    if (p.status === "fulfilled" && p.value) setCurrentPeriod(p.value.data);
    // Only surface a toast if ALL three failed — a partial 403 for pure
    // delegates is expected.
    if (r.status === "rejected" && a.status === "rejected" && p.status === "rejected") {
      toast.error(r.reason?.response?.data?.detail || "Failed to load reports");
    }
  };
  useEffect(() => { load(); }, []);

  const loadSummary = async () => {
    if (!currentPeriod) return;
    try {
      const { data } = await api.get(`/team/summary?period=${currentPeriod.period_number}`);
      setSummary(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load team summary");
    }
  };
  useEffect(() => { if (currentPeriod) loadSummary(); }, [currentPeriod]);

  const changePeriod = async (n) => {
    try {
      const { data } = await api.get(`/pay-periods/${n}`);
      setCurrentPeriod(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to change period");
    }
  };

  const approve = async (row) => {
    try {
      await api.post(`/approvals/${row.user.id}?period=${currentPeriod.period_number}`);
      toast.success(`Approved · ${row.user.display_name}`);
      await loadSummary();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const approveAllVisible = async () => {
    if (!currentPeriod) return;
    const targets = visibleRows.filter((r) => r.can_approve_l1 || r.can_approve_l2);
    if (targets.length === 0) {
      toast.info("Nothing to approve — no visible reports awaiting your approval.");
      return;
    }
    if (!window.confirm(
      `Approve ${targets.length} timesheet${targets.length === 1 ? "" : "s"} for Period ${currentPeriod.period_number}?`,
    )) return;
    setApprovingAll(true);
    let ok = 0, fail = 0;
    for (const row of targets) {
      try {
        await api.post(`/approvals/${row.user.id}?period=${currentPeriod.period_number}`);
        ok += 1;
      } catch { fail += 1; }
    }
    await loadSummary();
    setApprovingAll(false);
    if (fail === 0) toast.success(`Approved ${ok} timesheet${ok === 1 ? "" : "s"}`);
    else if (ok === 0) toast.error(`Failed to approve ${fail} timesheet${fail === 1 ? "" : "s"}`);
    else toast.warning(`Approved ${ok} · ${fail} failed`);
  };
  const revoke = async (row, level) => {
    if (!window.confirm(`Revoke level-${level} approval for ${row.user.display_name}?`)) return;
    try {
      await api.delete(`/approvals/${row.user.id}?period=${currentPeriod.period_number}&level=${level}`);
      toast.success("Revoked");
      await loadSummary();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const [nudgingId, setNudgingId] = useState(null);
  const nudgeL1 = async (b) => {
    if (!b.approver || !currentPeriod) return;
    setNudgingId(b.approver.id);
    try {
      await api.post(`/team/nudge-l1/${b.approver.id}?period=${currentPeriod.period_number}`);
      toast.success(`Nudge sent to ${b.approver.display_name}`);
      await loadSummary();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to send nudge");
    } finally {
      setNudgingId(null);
    }
  };

  const autoDistribute = async (row) => {
    const daysTxt = row.expectation?.deficit_hours
      ? ` (~${row.expectation.deficit_hours}h missing)`
      : "";
    if (!window.confirm(
      `Auto-distribute ${row.user.max_daily_hours}h to every scheduled work-day of ${row.user.display_name} that has NO sessions yet${daysTxt}?`,
    )) return;
    try {
      const { data } = await api.post(`/team/${row.user.id}/auto-distribute?period=${currentPeriod.period_number}`);
      if (data.created === 0) {
        toast.info(data.message || "Nothing to distribute.");
      } else {
        toast.success(data.message);
      }
      await loadSummary();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to auto-distribute.");
    }
  };

  const visibleRows = useMemo(() => {
    return (summary.rows || []).filter((r) => {
      if (!approvalSearch.trim()) return true;
      const q = approvalSearch.toLowerCase();
      return (
        r.user.display_name.toLowerCase().includes(q) ||
        (r.user.employee_number || "").toString().toLowerCase().includes(q) ||
        (r.user.department || "").toLowerCase().includes(q) ||
        (r.user.job_title || "").toLowerCase().includes(q)
      );
    });
  }, [summary.rows, approvalSearch]);

  // ----- Group rows by approval status (Pending / L1 / Approved) --------
  const groupedRows = useMemo(() => {
    const g = { pending: [], level_1: [], approved: [] };
    for (const r of visibleRows) {
      const s = r?.approval?.status;
      if (s === "approved") g.approved.push(r);
      else if (s === "level_1") g.level_1.push(r);
      else g.pending.push(r);
    }
    return g;
  }, [visibleRows]);

  // Per-group collapse — Pending open by default; other two collapsed.
  const [collapsedGroups, setCollapsedGroups] = useState({
    pending: false, level_1: true, approved: true,
  });
  const toggleGroup = (key) => setCollapsedGroups((prev) => ({ ...prev, [key]: !prev[key] }));

  // ---------------------------------------------------------------------
  // Sub-grouping by direct manager (asked for by Senior Managers approving
  // the whole company). When on, each status bucket is further split into
  // "Reports of {manager name}" sub-sections. Preference persisted to
  // localStorage so leaders don't retoggle every visit.
  // ---------------------------------------------------------------------
  const [groupByManager, setGroupByManager] = useState(() => {
    try { return localStorage.getItem("sa_team_group_by_manager") === "1"; } catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem("sa_team_group_by_manager", groupByManager ? "1" : "0"); } catch { /* ignore */ }
  }, [groupByManager]);
  const [groupByDept, setGroupByDept] = useState(() => {
    try { return localStorage.getItem("sa_team_group_by_dept") === "1"; } catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem("sa_team_group_by_dept", groupByDept ? "1" : "0"); } catch { /* ignore */ }
  }, [groupByDept]);
  const toggleGroupByManager = () => setGroupByManager((s) => { const n = !s; if (n) setGroupByDept(false); return n; });
  const toggleGroupByDept = () => setGroupByDept((s) => { const n = !s; if (n) setGroupByManager(false); return n; });
  const [collapsedManagers, setCollapsedManagers] = useState(new Set());
  const toggleManager = (key) => setCollapsedManagers((prev) => {
    const next = new Set(prev);
    next.has(key) ? next.delete(key) : next.add(key);
    return next;
  });

  const subgroupByManager = (rows) => {
    if (!rows || rows.length === 0) return [];
    const buckets = new Map();
    for (const r of rows) {
      const mgr = r.manager;
      const key = mgr?.id || "__unassigned__";
      const label = mgr?.display_name || "— Unassigned —";
      const jobTitle = mgr?.job_title || null;
      const bucket = buckets.get(key) || { key, label, jobTitle, rows: [] };
      bucket.rows.push(r);
      buckets.set(key, bucket);
    }
    return Array.from(buckets.values()).sort((a, b) => {
      // Unassigned bucket sinks to the bottom.
      if (a.key === "__unassigned__") return 1;
      if (b.key === "__unassigned__") return -1;
      return a.label.localeCompare(b.label);
    });
  };

  const subgroupByDept = (rows) => {
    const buckets = new Map();
    for (const r of rows) {
      const key = r.user.department || "— No department —";
      const b = buckets.get(key) || { key, rows: [] };
      b.rows.push(r);
      buckets.set(key, b);
    }
    return Array.from(buckets.values()).sort((a, b) => a.key.localeCompare(b.key));
  };

  // Deterministic, well-separated colours: departments sorted alphabetically
  // and mapped into a curated palette (no hash collisions for ≤10 depts).
  const deptColorMap = useMemo(() => {
    const names = Array.from(new Set((summary.rows || []).map((r) => r.user.department || "— No department —"))).sort();
    const m = new Map();
    names.forEach((n, i) => m.set(n, DEPT_PALETTE[i % DEPT_PALETTE.length]));
    return m;
  }, [summary]);
  const deptColor = (name) => deptColorMap.get(name || "— No department —") || DEPT_PALETTE[0];

  const [downloadingAll, setDownloadingAll] = useState(false);
  const [emailingAll, setEmailingAll] = useState(false);
  const [approvingAll, setApprovingAll] = useState(false);
  const downloadOne = async (row, { silent } = {}) => {
    if (!currentPeriod) return false;
    if (!silent) setDownloadingUserId(row.user.id);
    try {
      const qs = new URLSearchParams({
        period: String(currentPeriod.period_number),
        user_id: row.user.id,
      });
      const resp = await fetch(`${API_BASE}/timesheet/export?${qs.toString()}`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (!resp.ok) {
        let msg = "Failed to export timesheet";
        try {
          const body = await resp.json();
          if (body?.detail) msg = body.detail;
        } catch (parseErr) {
          console.warn("Non-JSON error response from /timesheet/export", parseErr);
        }
        throw new Error(msg);
      }
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const disp = resp.headers.get("Content-Disposition") || "";
      const match = disp.match(/filename="?([^";]+)"?/);
      a.href = url;
      a.download = match
        ? match[1]
        : `${(row.user.display_name || "").replace(/[^A-Za-z]/g, "")}${currentPeriod.period_number}.xlsm`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      // Delay revoke so browser has time to consume the URL for the download.
      setTimeout(() => URL.revokeObjectURL(url), 4000);
      if (!silent) toast.success(`Downloaded · ${row.user.display_name}`);
      return true;
    } catch (e) {
      if (!silent) toast.error(e?.message || "Failed to export timesheet");
      return false;
    } finally {
      if (!silent) setDownloadingUserId(null);
    }
  };

  // "Download All" — one ZIP file containing every visible timesheet.
  // Uses the existing `/timesheet/export-bulk` endpoint (same one the
  // "Email All" flow uses) instead of firing N individual .xlsm downloads,
  // which browsers rate-limit and users found messy.
  const downloadAllVisible = async () => {
    if (!currentPeriod || visibleRows.length === 0) return;
    setDownloadingAll(true);
    try {
      const qs = new URLSearchParams({
        period: String(currentPeriod.period_number),
        scope: "direct_reports",
        approved_only: "1",
      });
      const resp = await fetch(`${API_BASE}/timesheet/export-bulk?${qs.toString()}`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (!resp.ok) {
        let msg = "Failed to build timesheet bundle";
        try { const b = await resp.json(); if (b?.detail) msg = b.detail; }
        catch (parseErr) { console.warn("Non-JSON error response from /timesheet/export-bulk", parseErr); }
        throw new Error(msg);
      }
      const blob = await resp.blob();
      const included = Number(resp.headers.get("X-Included-Count") || visibleRows.length);
      const failedCount = Number(resp.headers.get("X-Failed-Count") || 0);
      const skippedCount = Number(resp.headers.get("X-Skipped-Count") || 0);
      const dispMatch = (resp.headers.get("Content-Disposition") || "").match(/filename="?([^";]+)"?/);
      const zipName = dispMatch ? dispMatch[1] : `summit-air-timesheets-P${currentPeriod.period_number}.zip`;

      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = zipName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 4000);

      if (failedCount === 0 && skippedCount === 0) {
        toast.success(`Downloaded ${zipName} · ${included} approved timesheet${included === 1 ? "" : "s"}`);
      } else if (failedCount === 0) {
        toast.warning(`Downloaded ${included} approved · ${skippedCount} skipped (not fully approved)`);
      } else {
        toast.warning(
          `Downloaded ${zipName} · ${included} included, ${failedCount} failed${skippedCount ? `, ${skippedCount} not fully approved` : ""}`,
        );
      }
    } catch (e) {
      toast.error(e?.message || "Could not download timesheet bundle");
    } finally {
      setDownloadingAll(false);
    }
  };

  // "Email All Timesheets" — downloads a ZIP bundle of every visible timesheet
  // and hands it off to the OS. Prefers the modern Web Share API (works in
  // Chrome, Safari, Edge — including desktop Chrome 89+, iOS Safari 15+ and
  // macOS Safari 13+): a share sheet opens with the ZIP already attached, so
  // "Mail" / "Outlook" / "Gmail" pre-fill the attachment automatically.
  //
  // Falls back to plain `mailto:` (attachment-less by RFC-6068) if the browser
  // can't share files — then the ZIP is downloaded and the user attaches it
  // to the drafted email manually. Kept as a fallback so nothing regresses on
  // older browsers.
  const emailAllTimesheets = async () => {
    if (!currentPeriod || visibleRows.length === 0) return;
    setEmailingAll(true);
    try {
      // 1) Fetch the ZIP from the backend.
      const qs = new URLSearchParams({
        period: String(currentPeriod.period_number),
        scope: "direct_reports",
      });
      const resp = await fetch(`${API_BASE}/timesheet/export-bulk?${qs.toString()}`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (!resp.ok) {
        let msg = "Failed to build timesheet bundle";
        try { const b = await resp.json(); if (b?.detail) msg = b.detail; }
        catch (parseErr) { console.warn("Non-JSON error response from /timesheet/export-bulk", parseErr); }
        throw new Error(msg);
      }
      const blob = await resp.blob();
      const included = Number(resp.headers.get("X-Included-Count") || visibleRows.length);
      const failedCount = Number(resp.headers.get("X-Failed-Count") || 0);
      const dispMatch = (resp.headers.get("Content-Disposition") || "").match(/filename="?([^";]+)"?/);
      const zipName = dispMatch ? dispMatch[1] : `summit-air-timesheets-P${currentPeriod.period_number}.zip`;

      // Prepare a File so the browser can share it as an attachment.
      const file = new File([blob], zipName, { type: "application/zip" });
      const engineerList = visibleRows
        .map((r) => `  • ${r.user.display_name} (#${r.user.employee_number})`)
        .join("\n");
      const subject = `Timesheets — Pay Period ${currentPeriod.period_number} (${currentPeriod.range_label || ""})`.trim();
      const bodyLines = [
        `Please find attached the timesheet bundle for Pay Period ${currentPeriod.period_number}.`,
        currentPeriod.range_label ? `Period range: ${currentPeriod.range_label}` : "",
        ``,
        `Attachment: ${zipName}  (${included} timesheet${included === 1 ? "" : "s"}${failedCount ? `, ${failedCount} failed` : ""})`,
        ``,
        `Included:`,
        engineerList,
      ].filter(Boolean);
      const body = bodyLines.join("\n");

      // 2a) Modern path — Web Share API with File attachment.
      if (typeof navigator !== "undefined"
          && typeof navigator.canShare === "function"
          && navigator.canShare({ files: [file] })
          && typeof navigator.share === "function") {
        try {
          await navigator.share({ files: [file], title: subject, text: body });
          toast.success(`Shared ${included} timesheet${included === 1 ? "" : "s"} via your email client.`);
          return;
        } catch (shareErr) {
          // User cancelled — do NOT fall back to mailto. Cancelling explicit.
          if (shareErr?.name === "AbortError") {
            return;
          }
          // Some browsers advertise `canShare` but still throw on share (e.g.
          // Chrome desktop when the file exceeds a security limit). Fall
          // through to the mailto fallback so the approver isn't blocked.
          console.warn("navigator.share failed, falling back to mailto:", shareErr);
        }
      }

      // 2b) Fallback path — trigger a browser download of the ZIP, then open
      // the user's mail client with a pre-filled subject + body (no
      // attachment — mailto has no attachment field). The body ends with a
      // clear instruction to attach the just-downloaded file.
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = zipName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 4000);

      const fallbackBody = [
        `Hi,`,
        ``,
        ...bodyLines,
        ``,
        `⚠  Attach the ZIP that was just downloaded to your device — this browser can't auto-attach files to email.`,
        ``,
        `Thanks,`,
        `${user?.display_name || ""}`,
      ].join("\n");
      const mailto = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(fallbackBody)}`;
      window.location.href = mailto;

      toast.success(`Bundle ready · ${included} timesheet${included === 1 ? "" : "s"}. Attach it to the email.`, { duration: 8000 });
    } catch (e) {
      toast.error(e?.message || "Failed to email timesheets");
    } finally {
      setEmailingAll(false);
    }
  };

  const assign = async (uid) => {
    try {
      await api.post(`/direct-reports/${uid}`);
      toast.success("Added to your direct reports");
      setOpen(false);
      setQuery("");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to assign");
    }
  };
  const unassign = async (uid) => {
    if (!window.confirm("Remove this user from your direct reports?")) return;
    try {
      await api.delete(`/direct-reports/${uid}`);
      toast.success("Removed");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to remove");
    }
  };

  const filtered = available.filter((u) => {
    if (u.manager_id === user.id) return false; // already yours (shows in reports section)
    if (!query) return true;
    const q = query.toLowerCase();
    return (
      (u.display_name || "").toLowerCase().includes(q) ||
      (u.employee_number || "").toLowerCase().includes(q) ||
      (u.department || "").toLowerCase().includes(q) ||
      (u.location || "").toLowerCase().includes(q)
    );
  });

  return (
    <div className="w-full mx-auto px-4 sm:px-[15%] py-10" data-testid="direct-reports-page">
      <div className="flex items-baseline justify-between flex-wrap gap-4">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500">Timesheets · MST</div>
          <h1 className="font-display text-4xl font-semibold tracking-tight mt-1">Direct reports</h1>
          <p className="text-sm text-gray-500 mt-1">Users who report to you ({user.job_title || roleLabel(user.role)}).</p>
        </div>

        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-summit-primary hover:bg-summit-primary-soft text-white" data-testid="open-add-report">
              <UserPlus className="w-4 h-4 mr-1.5" /> Add report
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader><DialogTitle>Add direct report</DialogTitle></DialogHeader>
            <div className="mt-2 relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <Input
                className="pl-9"
                placeholder="Search by name, employee number, department, location"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                data-testid="report-search"
                autoFocus
              />
            </div>
            <div className="mt-4 max-h-[420px] overflow-y-auto -mx-2">
              {filtered.length === 0 && (
                <div className="text-center text-sm text-gray-400 py-8">No matching users available.</div>
              )}
              {filtered.map((u) => (
                <button
                  key={u.id}
                  onClick={() => assign(u.id)}
                  className="w-full text-left px-3 py-2.5 flex items-center gap-3 hover:bg-summit-glacier rounded-md"
                  data-testid={`available-report-${u.employee_number}`}
                >
                  <div className="w-8 h-8 rounded-full bg-summit-primary text-white grid place-items-center font-mono text-xs">
                    {u.first_initial}{u.last_name?.[0] || ""}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium">{u.display_name}</div>
                    <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500">
                      #{u.employee_number} · {u.department} · {u.location || "—"}
                    </div>
                  </div>
                  <Badge variant="secondary" className="font-mono text-[10px]">{u.job_title}</Badge>
                </button>
              ))}
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="border border-summit-hairline rounded-lg p-5 bg-white">
          <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500 flex items-center gap-1.5">
            <Users className="w-3 h-3" /> Reports
          </div>
          <div className="font-mono text-4xl mt-2 tracking-tighter" data-testid="reports-count">{reports.length}</div>
        </div>
        <Link
          to="/team/on-duty"
          className="border border-summit-hairline rounded-lg p-5 bg-white hover:bg-summit-glacier-soft/50 transition-colors group"
          data-testid="team-quick-on-duty"
        >
          <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500 flex items-center gap-1.5">
            <span className="inline-block w-2 h-2 rounded-full bg-emerald-500 animate-pulse" /> Live
          </div>
          <div className="font-display text-lg font-medium tracking-tight mt-2 flex items-center justify-between">
            On-duty roster
            <ChevronRight className="w-4 h-4 text-gray-400 group-hover:text-summit-primary transition-colors" />
          </div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500 mt-1">Clock · Duty · Whole-day + map</div>
        </Link>
        <Link
          to="/team/on-vacation"
          className="border border-summit-hairline rounded-lg p-5 bg-white hover:bg-summit-glacier-soft/50 transition-colors group"
          data-testid="team-quick-on-vacation"
        >
          <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500 flex items-center gap-1.5">
            <span className="inline-block w-2 h-2 rounded-full bg-amber-500" /> Time off
          </div>
          <div className="font-display text-lg font-medium tracking-tight mt-2 flex items-center justify-between">
            On vacation
            <ChevronRight className="w-4 h-4 text-gray-400 group-hover:text-summit-primary transition-colors" />
          </div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500 mt-1">Vacation · Sick · Day off</div>
        </Link>
      </div>

      {/* Senior Manager / Admin — today's vacation headcount grouped by job position */}
      <VacationHeadcountBanner user={user} />

      {/* ---- L2 hierarchy view (Senior Manager / Admin only) ---- */}
      {canSeeHierarchy && (
        <div className="mt-10">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500 flex items-center gap-1.5">
                <GitBranch className="w-3 h-3" /> Reports hierarchy
              </div>
              <p className="text-xs text-gray-500 mt-0.5">
                See your direct reports&apos; direct reports — grouped by department and leader.
              </p>
            </div>
            <button
              type="button"
              onClick={() => setShowHierarchy((s) => !s)}
              className={`h-9 px-3 rounded-md border text-xs font-mono uppercase tracking-widest transition-colors ${
                showHierarchy
                  ? "bg-summit-primary text-white border-summit-primary"
                  : "bg-white text-summit-primary border-summit-hairline hover:bg-summit-glacier-soft/50"
              }`}
              data-testid="team-hierarchy-toggle"
              aria-pressed={showHierarchy}
            >
              {showHierarchy ? "Hide hierarchy" : "Show 2-level hierarchy"}
            </button>
          </div>
          {showHierarchy && currentPeriod?.period_number && (
            <TeamHierarchyView periodNumber={currentPeriod.period_number} />
          )}
        </div>
      )}

      {/* ---- Pay-period approvals ---- */}
      <div className="mt-10 border border-summit-hairline rounded-lg bg-white overflow-hidden" data-testid="team-approvals-section">
        <div className="px-5 py-4 border-b border-summit-hairline flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500 flex items-center gap-1.5">
              <ClipboardCheck className="w-3 h-3" /> Pay period approvals
            </div>
            <h2 className="font-display text-lg font-medium tracking-tight mt-0.5">
              Team summary
            </h2>
            {canSeeHierarchy && (
              <div className="mt-1.5 inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border border-purple-200 bg-purple-50 text-purple-800 font-mono text-[10px] uppercase tracking-widest" data-testid="team-summary-scope-banner">
                <Zap className="w-3 h-3" /> Company-wide · You can approve any timesheet (L1 &amp; L2)
              </div>
            )}
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <button
              type="button"
              onClick={toggleGroupByManager}
              className={`h-9 px-3 rounded-md border text-[10px] font-mono uppercase tracking-widest transition-colors inline-flex items-center gap-1.5 ${
                groupByManager
                  ? "bg-summit-primary text-white border-summit-primary"
                  : "bg-white text-summit-primary border-summit-hairline hover:bg-summit-glacier-soft/50"
              }`}
              data-testid="group-by-manager-toggle"
              aria-pressed={groupByManager}
              title="Group each status bucket by each employee's direct manager"
            >
              <GitBranch className="w-3.5 h-3.5" />
              {groupByManager ? "Grouped by manager" : "Group by manager"}
            </button>
            <button
              type="button"
              onClick={toggleGroupByDept}
              className={`h-9 px-3 rounded-md border text-[10px] font-mono uppercase tracking-widest transition-colors inline-flex items-center gap-1.5 ${
                groupByDept
                  ? "bg-summit-primary text-white border-summit-primary"
                  : "bg-white text-summit-primary border-summit-hairline hover:bg-summit-glacier-soft/50"
              }`}
              data-testid="group-by-dept-toggle"
              aria-pressed={groupByDept}
              title="Group each status bucket by department"
            >
              <Building2 className="w-3.5 h-3.5" />
              {groupByDept ? "Grouped by department" : "Group by department"}
            </button>
            <div className="relative">
              <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                value={approvalSearch}
                onChange={(e) => setApprovalSearch(e.target.value)}
                placeholder="Search reports…"
                className="h-9 pl-8 pr-3 rounded-md border border-summit-hairline text-xs bg-white w-52"
                data-testid="approval-search"
              />
            </div>
            <PayPeriodSelector period={currentPeriod} onChange={changePeriod} testId="approvals-period" />
            {(() => {
              const approvableCount = visibleRows.filter((r) => r.can_approve_l1 || r.can_approve_l2).length;
              return (
                <Button
                  variant="outline"
                  className="border-summit-emerald/60 bg-summit-emerald/10 text-summit-emerald hover:bg-summit-emerald hover:text-white h-9"
                  onClick={approveAllVisible}
                  disabled={approvingAll || approvableCount === 0}
                  title={approvableCount === 0
                    ? "No visible reports awaiting your approval"
                    : `Approve all ${approvableCount} visible timesheet${approvableCount === 1 ? "" : "s"}`}
                  data-testid="approve-all-btn"
                >
                  {approvingAll ? (
                    <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
                  ) : (
                    <CheckCheck className="w-4 h-4 mr-1.5" />
                  )}
                  Approve all ({approvableCount})
                </Button>
              );
            })()}
            <Button
              variant="outline"
              className="border-summit-hairline text-summit-primary hover:bg-summit-glacier h-9"
              onClick={downloadAllVisible}
              disabled={downloadingAll || visibleRows.length === 0}
              title="Download a ZIP of every FULLY APPROVED timesheet (.xlsm) — pending ones are skipped"
              data-testid="download-all-timesheets-btn"
            >
              {downloadingAll ? (
                <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
              ) : (
                <Download className="w-4 h-4 mr-1.5" />
              )}
              Download all ({visibleRows.length})
            </Button>
            <Button
              variant="outline"
              className="border-summit-hairline text-summit-primary hover:bg-summit-glacier h-9"
              onClick={emailAllTimesheets}
              disabled={emailingAll || visibleRows.length === 0}
              title="Bundle every visible timesheet into a ZIP and open your email client — attach the downloaded file to send."
              data-testid="email-all-timesheets-btn"
            >
              {emailingAll ? (
                <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
              ) : (
                <Mail className="w-4 h-4 mr-1.5" />
              )}
              Email all
            </Button>
          </div>
        </div>

        {(summary.l1_banners || []).length > 0 && (
          <div className="border-b border-summit-hairline" data-testid="l1-pending-banners">
            {summary.l1_banners.map((b) => (
              <div
                key={b.approver?.id || "unassigned"}
                className="px-5 py-3 flex items-center gap-3 flex-wrap bg-amber-50/70 border-b border-amber-100 last:border-b-0"
                data-testid={`l1-banner-${b.approver?.id || "unassigned"}`}
              >
                <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-summit-ink">
                    <span className="font-medium">{b.approver ? b.approver.display_name : "— No manager assigned —"}</span>
                    {b.approver?.job_title && (
                      <span className="font-mono text-[10px] uppercase tracking-widest text-gray-500"> · {b.approver.job_title}</span>
                    )}
                    <span className="text-amber-800"> — {b.count} timesheet{b.count === 1 ? "" : "s"} awaiting L1 approval</span>
                  </div>
                  <div className="text-[11px] text-gray-500 truncate">{(b.employees || []).join(" · ")}</div>
                </div>
                {b.approver && (
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-8 border-amber-300 bg-white text-amber-800 hover:bg-amber-100"
                    disabled={!b.can_nudge || nudgingId === b.approver.id}
                    onClick={() => nudgeL1(b)}
                    title={b.can_nudge
                      ? "Send a push + bell notification asking them to approve their queue"
                      : "Nudged within the last hour — try again later"}
                    data-testid={`l1-nudge-btn-${b.approver.id}`}
                  >
                    {nudgingId === b.approver.id
                      ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                      : <BellRing className="w-3.5 h-3.5 mr-1.5" />}
                    {b.can_nudge ? "Nudge" : "Nudged"}
                  </Button>
                )}
              </div>
            ))}
          </div>
        )}

        {summary.rows.length === 0 ? (
          <div className="p-10 text-center text-sm text-gray-400" data-testid="empty-team-summary">
            No direct or second-tier reports to approve.
          </div>
        ) : (
          <div className="divide-y divide-summit-hairline">
            {[
              { key: "pending",  label: "Pending",           dot: "bg-amber-500",   testid: "pending" },
              { key: "level_1",  label: "Level-1 approved",  dot: "bg-sky-500",     testid: "level-1" },
              { key: "approved", label: "Fully approved",    dot: "bg-emerald-500", testid: "approved" },
            ].map((grp) => {
              const allRows = groupedRows[grp.key] || [];
              const deptL2Rows = groupByDept ? [] : allRows.filter((r) => r.dept_l2_row);
              const rows = groupByDept ? allRows : allRows.filter((r) => !r.dept_l2_row);
              const isCollapsed = !!collapsedGroups[grp.key];
              const renderDeptGroups = (rowsArr) => subgroupByDept(rowsArr).map((sub) => (
                <div key={sub.key} data-testid={`approval-dept-group-${grp.testid}-${sub.key}`}>
                  <div
                    className="pl-6 pr-4 py-2 flex items-center gap-2.5 bg-summit-glacier-soft/40"
                    style={{ borderLeft: `4px solid ${deptColor(sub.key)}` }}
                    data-testid={`approval-dept-header-${grp.testid}-${sub.key}`}
                  >
                    <span className="w-2 h-2 rounded-sm shrink-0" style={{ background: deptColor(sub.key) }} />
                    <span className="text-xs font-medium text-summit-ink truncate">{sub.key}</span>
                    <span className="ml-auto font-mono text-[10px] uppercase tracking-widest text-gray-500">
                      {sub.rows.length}
                    </span>
                  </div>
                  <div className="divide-y divide-summit-hairline">
                    {sub.rows.map((row) => (
                      <div key={row.user.id} style={{ borderLeft: `4px solid ${deptColor(sub.key)}` }}>
                        <ApprovalRow
                          row={row}
                          viewer={user}
                          periodNumber={currentPeriod.period_number}
                          onApprove={approve}
                          onRevoke={revoke}
                          onAutoDistribute={autoDistribute}
                          onDownload={downloadOne}
                          downloading={downloadingUserId === row.user.id}
                          expanded={expandedRowIds.has(row.user.id)}
                          onToggle={() => toggleRow(row.user.id)}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              ));
              return (
                <div key={grp.key} data-testid={`approval-group-${grp.testid}`}>
                  <button
                    type="button"
                    className="w-full px-4 py-2.5 flex items-center gap-3 bg-summit-canvas hover:bg-summit-glacier-soft/60 transition-colors text-left"
                    onClick={() => toggleGroup(grp.key)}
                    data-testid={`approval-group-toggle-${grp.testid}`}
                    aria-expanded={!isCollapsed}
                  >
                    <ChevronRight className={`w-4 h-4 text-gray-400 transition-transform ${isCollapsed ? "rotate-0" : "rotate-90"}`} />
                    <span className={`w-1.5 h-1.5 rounded-full ${grp.dot}`} />
                    <span className="font-mono text-[10px] uppercase tracking-widest text-summit-ink font-medium">
                      {grp.label}
                    </span>
                    <span
                      className="font-mono text-[10px] uppercase tracking-widest text-gray-500"
                      data-testid={`approval-group-count-${grp.testid}`}
                    >
                      · {allRows.length}
                    </span>
                  </button>
                  {!isCollapsed && (
                    allRows.length === 0 ? (
                      <div
                        className="px-6 py-3 text-xs text-gray-400 italic"
                        data-testid={`approval-group-empty-${grp.testid}`}
                      >
                        None in this group.
                      </div>
                    ) : (
                      <>
                        {rows.length > 0 && (groupByDept ? (
                      <>{renderDeptGroups(rows)}</>
                    ) : groupByManager ? (
                      // Sub-grouped by direct manager.
                      <div className="divide-y divide-summit-hairline">
                        {subgroupByManager(rows).map((sub) => {
                          const subKey = `${grp.key}::${sub.key}`;
                          const subCollapsed = collapsedManagers.has(subKey);
                          return (
                            <div key={subKey} data-testid={`approval-mgr-group-${grp.testid}-${sub.key}`}>
                              <button
                                type="button"
                                className="w-full pl-10 pr-4 py-2 flex items-center gap-2.5 bg-summit-glacier-soft/40 hover:bg-summit-glacier-soft transition-colors text-left"
                                onClick={() => toggleManager(subKey)}
                                data-testid={`approval-mgr-group-toggle-${grp.testid}-${sub.key}`}
                                aria-expanded={!subCollapsed}
                              >
                                <ChevronRight className={`w-3.5 h-3.5 text-gray-400 transition-transform ${subCollapsed ? "rotate-0" : "rotate-90"}`} />
                                <span className="text-xs font-medium text-summit-ink truncate">
                                  Reports of {sub.label}
                                </span>
                                {sub.jobTitle && (
                                  <span className="font-mono text-[9px] uppercase tracking-widest text-gray-500 truncate">
                                    · {sub.jobTitle}
                                  </span>
                                )}
                                <span
                                  className="ml-auto font-mono text-[10px] uppercase tracking-widest text-gray-500"
                                  data-testid={`approval-mgr-group-count-${grp.testid}-${sub.key}`}
                                >
                                  {sub.rows.length}
                                </span>
                              </button>
                              {!subCollapsed && (
                                <div className="divide-y divide-summit-hairline">
                                  {sub.rows.map((row) => (
                                    <div key={row.user.id} style={{ borderLeft: `4px solid ${deptColor(row.user.department || "— No department —")}` }}>
                                      <ApprovalRow
                                        row={row}
                                        viewer={user}
                                        periodNumber={currentPeriod.period_number}
                                        onApprove={approve}
                                        onRevoke={revoke}
                                        onAutoDistribute={autoDistribute}
                                        onDownload={downloadOne}
                                        downloading={downloadingUserId === row.user.id}
                                        expanded={expandedRowIds.has(row.user.id)}
                                        onToggle={() => toggleRow(row.user.id)}
                                      />
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="divide-y divide-summit-hairline">
                        {rows.map((row) => (
                          <div key={row.user.id} style={{ borderLeft: `4px solid ${deptColor(row.user.department || "— No department —")}` }}>
                            <ApprovalRow
                              row={row}
                              viewer={user}
                              periodNumber={currentPeriod.period_number}
                              onApprove={approve}
                              onRevoke={revoke}
                              onAutoDistribute={autoDistribute}
                              onDownload={downloadOne}
                              downloading={downloadingUserId === row.user.id}
                              expanded={expandedRowIds.has(row.user.id)}
                              onToggle={() => toggleRow(row.user.id)}
                            />
                          </div>
                        ))}
                      </div>
                    ))}
                        {deptL2Rows.length > 0 && renderDeptGroups(deptL2Rows)}
                      </>
                    )
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="mt-10 border border-summit-hairline rounded-lg bg-white overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-gray-500 border-b border-summit-hairline">
              <th className="px-4 py-2 font-mono text-[10px] uppercase tracking-widest">Name</th>
              <th className="px-4 py-2 font-mono text-[10px] uppercase tracking-widest">Emp #</th>
              <th className="px-4 py-2 font-mono text-[10px] uppercase tracking-widest">Department</th>
              <th className="px-4 py-2 font-mono text-[10px] uppercase tracking-widest">Job Title</th>
              <th className="px-4 py-2 font-mono text-[10px] uppercase tracking-widest">Location</th>
              <th className="px-4 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {reports.length === 0 && (
              <tr><td colSpan="6" className="px-4 py-10 text-center text-sm text-gray-400">
                No direct reports yet. Click &quot;Add report&quot; to assign users from the list.
              </td></tr>
            )}
            {reports.map((u) => (
              <tr key={u.id} className="border-b border-summit-hairline last:border-0" data-testid={`report-row-${u.employee_number}`}>
                <td className="px-4 py-2.5">{u.display_name}</td>
                <td className="px-4 py-2.5 font-mono">{u.employee_number}</td>
                <td className="px-4 py-2.5 text-xs">{u.department}</td>
                <td className="px-4 py-2.5 text-xs"><Badge variant="secondary" className="font-mono text-[10px]">{u.job_title}</Badge></td>
                <td className="px-4 py-2.5 font-mono text-xs">{u.location || "—"}</td>
                <td className="px-4 py-2.5 text-right">
                  <button onClick={() => unassign(u.id)} className="p-1.5 rounded-md hover:bg-rose-50 text-rose-600" title="Remove" data-testid={`unassign-report-${u.employee_number}`}>
                    <X className="w-4 h-4"/>
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Approval row component
// ---------------------------------------------------------------------------
// Compact expandable approval row — one line per report by default. Click the
// chevron (or the row) to expand for signoff detail & breakdown.
// ---------------------------------------------------------------------------
function ApprovalRow({ row, viewer, periodNumber, onApprove, onRevoke, onAutoDistribute, onDownload, downloading, expanded, onToggle }) {
  const st = row.approval.status;
  const l1 = row.approval.level_1;
  const l2 = row.approval.level_2;
  const isAdmin = viewer.role === "admin";
  const isFullyApproved = st === "approved" && row.approval.has_two_tiers && l1 && l2;
  const singleTierApproved = st === "approved" && !row.approval.has_two_tiers && l1;
  const canAutoDistribute = !isFullyApproved && !singleTierApproved && (row.can_approve_l1 || row.can_approve_l2 || isAdmin);
  const exp = row.expectation || {};
  const pd = row.per_diems || { cdn_days: [], int_days: [], cdn_amount: 0, int_amount: 0 };
  const cdnDays = (pd.cdn_days || []).length;
  const intDays = (pd.int_days || []).length;
  const adHrs = Number(row.totals.AD || 0);

  const statusStyle =
    st === "approved" ? "bg-emerald-100 text-emerald-800"
    : st === "level_1" ? "bg-sky-100 text-sky-800"
    : "bg-gray-100 text-gray-700";
  const statusLabel =
    st === "approved" ? (row.approval.has_two_tiers ? "L1 ✓ L2 ✓" : "Approved")
    : st === "level_1" ? "L1 ✓ · L2 —"
    : "Pending";
  const revokeL1Allowed = l1 && (isAdmin || l1.approver_id === viewer.id);
  const revokeL2Allowed = l2 && (isAdmin || l2.approver_id === viewer.id);

  const hasExpectation = exp.expected_hours > 0;
  const totalHrs = hasExpectation ? exp.regular_hours : row.totals.total_hours;
  const targetHrs = hasExpectation ? exp.expected_hours : null;
  const targetClass = hasExpectation
    ? (exp.is_below_target ? "text-amber-700" : "text-emerald-700")
    : "text-gray-700";
  const chevronRot = expanded ? "rotate-90" : "rotate-0";

  return (
    <div className="group" data-testid={`approval-row-${row.user.employee_number}`}>
      {/* -------- Compact header (single line per report) -------- */}
      <div
        className="px-4 py-2.5 flex items-center gap-4 flex-wrap cursor-pointer hover:bg-summit-glacier-soft/70 transition-colors"
        onClick={onToggle}
        data-testid={`approval-header-${row.user.employee_number}`}
      >
        <button
          type="button"
          className="shrink-0 p-1 -m-1 rounded hover:bg-summit-glacier"
          onClick={(e) => { e.stopPropagation(); onToggle(); }}
          aria-label={expanded ? "Collapse" : "Expand"}
          data-testid={`approval-toggle-${row.user.employee_number}`}
        >
          <ChevronRight className={`w-4 h-4 text-gray-400 transition-transform ${chevronRot}`} />
        </button>

        {/* Name + identity */}
        <div className="min-w-0 flex-1 flex items-center gap-2 flex-wrap">
          <span className="font-display text-sm font-medium tracking-tight text-summit-ink whitespace-nowrap">
            {row.user.display_name}
          </span>
          <span className="font-mono text-[10px] uppercase tracking-widest text-gray-400 whitespace-nowrap">
            #{row.user.employee_number}
          </span>
          <span className="font-mono text-[10px] uppercase tracking-widest text-gray-400 whitespace-nowrap">
            · {row.user.department || "—"}
          </span>
          {row.acting_as && (
            <span
              className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-indigo-100 text-indigo-800 font-mono text-[9px] uppercase tracking-widest border border-indigo-200"
              title={`You&apos;re acting on behalf of ${row.acting_as.name} for this report`}
              data-testid={`acting-as-${row.user.employee_number}`}
            >
              Acting for {row.acting_as.name}
            </span>
          )}
          {(() => {
            // Tier sub-label: Direct / L2 / Admin. Skipped when acting_as
            // already tells the story (delegate acting on someone's behalf).
            if (row.acting_as) return null;
            // Departmental L1 — caller is a same-department Supervisor/Manager
            // (not the direct manager). Takes precedence over Direct/L2/Admin.
            if (row.is_dept_l1) {
              return (
                <span
                  className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full font-mono text-[9px] uppercase tracking-widest border bg-teal-50 text-teal-800 border-teal-200"
                  title={`Departmental L1 approver — same department as ${row.user.display_name}`}
                  data-testid={`tier-dept-${row.user.employee_number}`}
                >
                  Dept L1
                </span>
              );
            }
            const l1id = row.approval?.level_1_approver?.id;
            const l2id = row.approval?.level_2_approver?.id;
            let tier = null;
            if (l1id && l1id === viewer.id) tier = { key: "direct", label: "Direct", cls: "bg-summit-glacier text-summit-primary border-summit-hairline", tip: "You are this employee's direct manager (L1 approver)" };
            else if (l2id && l2id === viewer.id) tier = { key: "l2", label: "L2", cls: "bg-slate-100 text-slate-700 border-slate-200", tip: "You are this employee's L2 approver" };
            else if (isAdmin) tier = { key: "org", label: "Org-wide", cls: "bg-amber-50 text-amber-800 border-amber-200", tip: "You can approve this row via your company-wide privileges (Admin / Senior Manager) — not the employee's role" };
            if (!tier) return null;
            return (
              <span
                className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full font-mono text-[9px] uppercase tracking-widest border ${tier.cls}`}
                data-testid={`tier-${tier.key}-${row.user.employee_number}`}
                title={tier.tip}
              >
                {tier.label}
              </span>
            );
          })()}
        </div>

        {/* Total hours vs target */}
        <div className="flex items-center gap-1.5 shrink-0" data-testid={`total-${row.user.employee_number}`}>
          <span className={`font-mono tabular-nums text-sm ${targetClass}`}>
            {totalHrs?.toFixed ? totalHrs.toFixed(2) : (totalHrs || 0)}
            {targetHrs != null && (
              <span className="text-gray-400">/{targetHrs}</span>
            )}
            <span className="text-[10px] text-gray-400 ml-0.5">h</span>
          </span>
          {adHrs > 0 && (
            <span
              className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-summit-glacier text-summit-primary font-mono text-[9px] uppercase tracking-widest border border-summit-hairline"
              title={`Stat holiday hours: ${adHrs}h (auto-filled)`}
              data-testid={`stat-holiday-${row.user.employee_number}`}
            >
              STAT {adHrs}h
            </span>
          )}
          {hasExpectation && exp.is_below_target && (
            <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800 font-mono text-[9px] uppercase tracking-widest" data-testid={`below-target-${row.user.employee_number}`}>
              <AlertTriangle className="w-2.5 h-2.5" /> {exp.deficit_hours}h
            </span>
          )}
          {hasExpectation && !exp.is_below_target && (
            <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-800 font-mono text-[9px] uppercase tracking-widest" data-testid={`on-target-${row.user.employee_number}`}>
              <Check className="w-2.5 h-2.5" /> ok
            </span>
          )}
        </div>

        {/* Per-diem badges */}
        <div className="flex items-center gap-1.5 shrink-0 min-w-[110px]" data-testid={`perdiem-${row.user.employee_number}`}>
          {cdnDays > 0 && (
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-indigo-50 text-indigo-700 font-mono text-[9px] uppercase tracking-widest border border-indigo-100"
              title={`Canadian per diem · ${cdnDays} day${cdnDays === 1 ? "" : "s"} · $${pd.cdn_amount.toFixed(2)}`}>
              <Wallet className="w-2.5 h-2.5" /> CDN {cdnDays}d · ${Math.round(pd.cdn_amount)}
            </span>
          )}
          {intDays > 0 && (
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-fuchsia-50 text-fuchsia-700 font-mono text-[9px] uppercase tracking-widest border border-fuchsia-100"
              title={`International per diem · ${intDays} day${intDays === 1 ? "" : "s"} · $${pd.int_amount.toFixed(2)}`}>
              <Wallet className="w-2.5 h-2.5" /> INT {intDays}d · ${Math.round(pd.int_amount)}
            </span>
          )}
          {cdnDays === 0 && intDays === 0 && (
            <span className="font-mono text-[9px] uppercase tracking-widest text-gray-300">— no per-diem —</span>
          )}
        </div>

        {/* Approval status */}
        <span className={`shrink-0 px-2 py-0.5 rounded-full font-mono text-[10px] uppercase tracking-widest ${statusStyle}`}
          data-testid={`approval-status-${row.user.employee_number}`}>
          {statusLabel}
        </span>

        {/* Action buttons — icon-only for compactness, kept clickable independent of row toggle */}
        <div className="flex items-center gap-1 shrink-0" onClick={(e) => e.stopPropagation()}>
          {row.can_approve_l1 && (
            <Button size="sm" className="h-7 px-2 bg-summit-primary hover:bg-summit-primary-soft text-white text-[11px]"
              onClick={() => onApprove(row)} data-testid={`approve-l1-${row.user.employee_number}`}>
              <Check className="w-3.5 h-3.5 mr-1" /> Approve
            </Button>
          )}
          {row.can_approve_l2 && (
            <Button size="sm" className="h-7 px-2 bg-emerald-700 hover:bg-emerald-800 text-white text-[11px]"
              onClick={() => onApprove(row)} data-testid={`approve-l2-${row.user.employee_number}`}>
              <CheckCheck className="w-3.5 h-3.5 mr-1" /> Final
            </Button>
          )}
          {canAutoDistribute && (
            <Button size="sm" variant="outline" className="h-7 px-2 border-summit-hairline text-summit-primary-soft hover:bg-summit-glacier text-[11px]"
              onClick={() => onAutoDistribute(row)}
              title="Fill scheduled work-days that have no sessions with the user's max daily hours"
              data-testid={`auto-distribute-${row.user.employee_number}`}>
              <Zap className="w-3.5 h-3.5" />
            </Button>
          )}
          <Button size="sm" variant="outline" className="h-7 px-2 border-summit-hairline text-summit-primary-soft hover:bg-summit-glacier text-[11px]"
            onClick={() => onDownload(row)}
            disabled={downloading}
            title={`Download ${row.user.display_name}'s timesheet (${(row.user.display_name || "").replace(/[^A-Za-z]/g, "")}${row.approval?.period_number || ""}.xlsm)`}
            data-testid={`download-timesheet-${row.user.employee_number}`}>
            {downloading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
          </Button>
        </div>
      </div>

      {/* -------- Expanded detail -------- */}
      {expanded && (
        <div className="px-6 pb-4 pt-1 bg-summit-canvas border-t border-summit-hairline" data-testid={`approval-expanded-${row.user.employee_number}`}>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3 max-w-3xl mb-3">
            <Stat label="Total" val={totalHrs} unit="h" />
            <Stat label="Aircraft" val={row.totals.aircraft_hours} unit="h" />
            <Stat label="AC · Adm" val={row.totals.AC} unit="h" />
            <Stat label="AD · Stat" val={row.totals.AD} unit="h" />
            <Stat label="OT" val={row.totals.AR} unit="h" />
            <Stat label="Vacation" val={row.totals.VAC} unit="h" />
            <Stat label="Sick" val={row.totals.SICK} unit="h" />
          </div>
          {(cdnDays > 0 || intDays > 0) && (
            <div className="mb-3 font-mono text-[10px] uppercase tracking-widest text-gray-500 flex items-center gap-3 flex-wrap">
              <Wallet className="w-3 h-3" />
              {cdnDays > 0 && <span>CDN per-diem · {cdnDays} day{cdnDays === 1 ? "" : "s"} · ${pd.cdn_amount.toFixed(2)}</span>}
              {intDays > 0 && <span>INT per-diem · {intDays} day{intDays === 1 ? "" : "s"} · ${pd.int_amount.toFixed(2)}</span>}
            </div>
          )}
          {row.timesheet_note?.note && (
            <div className="mb-3 flex items-start gap-2 px-3 py-2 rounded-md bg-amber-50 border border-amber-200 max-w-3xl" data-testid={`approval-note-${row.user.employee_number}`}>
              <StickyNote className="w-3.5 h-3.5 text-amber-700 mt-0.5 shrink-0" />
              <div className="min-w-0">
                <div className="font-mono text-[9px] uppercase tracking-widest text-amber-700">Timesheet note</div>
                <div className="text-xs text-amber-900 whitespace-pre-wrap break-words">{row.timesheet_note.note}</div>
              </div>
            </div>
          )}
          <div className="mb-3">
            <Link
              to={`/timesheet?user_id=${row.user.id}`}
              className="inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-widest text-summit-primary hover:text-summit-primary-soft transition-colors"
              data-testid={`view-timesheet-${row.user.employee_number}`}
              title="Open this report's timesheet — assign stat-pay columns, review daily totals"
            >
              <ExternalLink className="w-3 h-3" /> View timesheet
            </Link>
          </div>
          <div className="grid md:grid-cols-2 gap-3 max-w-3xl">
            <SigCell
              title="Crew Chief Signoff"
              level={1}
              entry={l1}
              expectedApprover={row.approval.level_1_approver}
              canRevoke={revokeL1Allowed}
              onRevoke={() => onRevoke(row, 1)}
              testid={`sig-l1-${row.user.employee_number}`}
            />
            <SigCell
              title="Manager Sign Off"
              level={2}
              entry={l2}
              expectedApprover={row.approval.level_2_approver}
              canRevoke={revokeL2Allowed}
              onRevoke={() => onRevoke(row, 2)}
              testid={`sig-l2-${row.user.employee_number}`}
            />
          </div>
        </div>
      )}
    </div>
  );
}

const Stat = ({ label, val, unit }) => (
  <div>
    <div className="font-mono text-[9px] uppercase tracking-widest text-gray-500">{label}</div>
    <div className="font-mono text-lg tabular-nums text-summit-ink mt-0.5">
      {(val || 0).toFixed ? val.toFixed(2) : val}
      <span className="ml-1 font-mono text-[10px] text-gray-400">{unit}</span>
    </div>
  </div>
);

const SigCell = ({ title, entry, expectedApprover, canRevoke, onRevoke, testid }) => (
  <div className={`border rounded-md p-3 ${entry ? "border-emerald-200 bg-emerald-50/50" : "border-dashed border-summit-hairline"}`} data-testid={testid}>
    <div className="flex items-center justify-between">
      <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500">{title}</div>
      {canRevoke && (
        <button
          type="button"
          onClick={onRevoke}
          className="p-1 rounded-md hover:bg-rose-50 text-rose-600"
          title="Revoke approval"
          data-testid={`${testid}-revoke`}
        >
          <Undo2 className="w-3.5 h-3.5" />
        </button>
      )}
    </div>
    {entry ? (
      <div className="mt-1">
        <div className="text-sm font-medium">{entry.approver_name}</div>
        {entry.on_behalf_of_name && (
          <div className="font-mono text-[10px] uppercase tracking-widest text-indigo-700" data-testid="on-behalf-of">
            on behalf of {entry.on_behalf_of_name}
          </div>
        )}
        <div className="font-mono text-[10px] text-gray-500">
          {new Date(entry.approved_at).toLocaleString()}
        </div>
      </div>
    ) : (
      <div className="mt-1 text-sm text-gray-400 italic">
        {expectedApprover ? `Awaiting ${expectedApprover.name}` : "N/A"}
      </div>
    )}
  </div>
);
